// DocumentCard.js
import React, { useState } from 'react';

const DocumentCard = ({ document, isOpen, onCardClick, searchTerms }) => {
    const [isMetadataVisible, setMetadataVisible] = useState(false);
    const [tablesVisibility, setTablesVisibility] = useState({
        keyFeatures: document.key_features.map(() => false),
        searchStrategies: document.search_strategies.map(() => false),
    });

    // State to manage metadata expanded/collapsed
    const [expandedMetadata, setExpandedMetadata] = useState({});

    const hasMetadata = () => {
        return document.metadata &&
            Object.keys(document.metadata).length > 0 &&
            Object.values(document.metadata).some(value => value !== null && value !== '');
    };

    // Helper function to check if tables exist and have content
    const hasTableContent = (tables) => {
        return tables &&
            Array.isArray(tables) &&
            tables.length > 0 &&
            tables.some(table => table.header && table.data);
    };

    const toggleMetadata = () => setMetadataVisible(!isMetadataVisible);

    const toggleTableVisibility = (type, index) => {
        const newVisibility = { ...tablesVisibility };
        newVisibility[type][index] = !newVisibility[type][index];
        setTablesVisibility(newVisibility);
    };

    const handleCardClick = (e) => {
        e.stopPropagation();
        onCardClick(document.docket_number);
    };

    const handleTableHeaderClick = (type, index, e) => {
        e.stopPropagation();
        toggleTableVisibility(type, index);
    };

    const handleExpandMetadata = (key) => {
        setExpandedMetadata((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const truncateText = (text, limit) => {
        return text.length > limit ? `${text.slice(0, limit)}...` : text;
    };

    // Array of highlight colors for different keywords
    const highlightColors = [
        'bg-blue-300', 'bg-cyan-300', 'bg-sky-300', 'bg-indigo-300',
        'bg-lightBlue-300', 'bg-teal-300', 'bg-aqua-300',
        'bg-yellow-300', 'bg-green-300', 'bg-red-300',
        'bg-purple-300', 'bg-pink-300', 'bg-orange-300',
        'bg-lime-300', 'bg-amber-300', 'bg-emerald-300',
        'bg-fuchsia-300', 'bg-rose-300', 'bg-violet-300'
    ];

    // Helper function to escape special characters in search terms for regex
    const escapeRegExp = (string) => {
        return string.replace(/[.*+?^${}()|[\]\\&/!]/g, '');
    };

    // Highlight text function to highlight search terms with different colors
    const highlightText = (text, terms) => {
        if (!terms || terms.length === 0 || typeof text !== 'string') {
            return text;
        }

        // Create a map of term to color
        const termColorMap = {};
        terms.forEach((term, index) => {
            termColorMap[term.toLowerCase()] = highlightColors[index % highlightColors.length];
        });

        // let result = text;
        let fragments = [{ text, isHighlighted: false, color: null }];

        // Process each term and split fragments further
        terms.forEach(term => {
            const pattern = new RegExp(escapeRegExp(term), 'gi');
            const newFragments = [];

            // Process each existing fragment
            fragments.forEach(fragment => {
                // Skip already highlighted fragments
                if (fragment.isHighlighted) {
                    newFragments.push(fragment);
                    return;
                }

                // Split non-highlighted fragment
                const parts = fragment.text.split(pattern);
                if (parts.length === 1) {
                    // No match found, keep fragment as is
                    newFragments.push(fragment);
                } else {
                    // Alternate between text and matches
                    for (let i = 0; i < parts.length; i++) {
                        if (parts[i]) {
                            newFragments.push({
                                text: parts[i],
                                isHighlighted: false,
                                color: null
                            });
                        }
                        // Add the matched term if not the last iteration
                        if (i < parts.length - 1) {
                            const match = text.match(pattern)[i % (text.match(pattern).length)];
                            newFragments.push({
                                text: match,
                                isHighlighted: true,
                                color: termColorMap[term.toLowerCase()]
                            });
                        }
                    }
                }
            });

            fragments = newFragments;
        });

        // Convert fragments to React elements
        return fragments.map((fragment, i) => {
            if (fragment.isHighlighted) {
                return <span key={i} className={`${fragment.color} text-black font-medium`}>{fragment.text}</span>;
            }
            return fragment.text;
        });
    };

    // Helper function to highlight text in table cells
    const highlightTableCell = (cell, terms) => {
        if (typeof cell !== 'string') {
            return cell;
        }
        return highlightText(cell, terms);
    };

    return (
        <div
            className={`p-2 rounded-lg shadow-md mb-3.5 max-w-screen-xl mx-auto transition-all duration-400 border-2 ${isOpen ? 'bg-blue-50 border-blue-500 shadow-lg' : 'border-gray-300 bg-gray-100'}`}
        >

            {/* Title and Docket Number */}
            <h3
                className={`text-lg font-bold mb-1 mt-1 cursor-pointer ${isOpen ? 'text-gray-700' : 'hover:text-blue-800'}`}
                onClick={handleCardClick}
            >
                {document.docket_number} - {document.title ? highlightText(document.title, searchTerms) : 'No Title'}
            </h3>

            {/* Show/Hide Metadata Button */}
            {isOpen && hasMetadata() && (
                <button
                    onClick={toggleMetadata}
                    className="text-blue-500 hover:text-blue-700 font-medium mb-4 block transition-colors"
                >
                    {isMetadataVisible ? 'Hide Metadata' : 'Show Metadata'}
                </button>
            )}

            {/* Metadata */}
            {isOpen && isMetadataVisible && hasMetadata() && (
                <div className="metadata bg-gray-50 p-4 rounded-lg shadow-sm mb-4">
                    <h4 className="text-lg font-semibold text-gray-600 mb-2">Metadata:</h4>
                    <ul className="list-disc pl-6 space-y-2 text-gray-500">
                        {Object.entries(document.metadata).map(([key, value], idx) => (
                            <li key={idx} className="flex justify-between">
                                <span className="font-medium text-gray-700">{key}:</span>
                                <span>
                                    {/* Truncate and highlight text if it's long */}
                                    {typeof value === 'string' && value.length > 130 ? (
                                        <>
                                            {/* Display truncated value or expanded value with highlighting */}
                                            {expandedMetadata[key] ? (
                                                highlightText(value, searchTerms)
                                            ) : (
                                                highlightText(truncateText(value, 130), searchTerms)
                                            )}
                                            {/* See more / See less toggle */}
                                            <button
                                                onClick={() => handleExpandMetadata(key)}
                                                className="text-blue-500 text-sm ml-2"
                                            >
                                                {expandedMetadata[key] ? 'See less' : 'See more'}
                                            </button>
                                        </>
                                    ) : (
                                        highlightText(value, searchTerms)
                                    )}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Key Features Tables */}
            {isOpen && hasTableContent(document.key_features) && document.key_features.length > 0 && document.key_features.map((table, index) => (
                <div key={`keyFeature-${index}`} className="bg-white p-4 rounded-lg shadow-md mb-4">
                    <h4
                        className="text-lg font-semibold text-gray-700 mb-2 cursor-pointer"
                        onClick={(e) => handleTableHeaderClick('keyFeatures', index, e)} // Handle key features header click
                    >
                        Key Features {index + 1} <i className={`fas ${tablesVisibility.keyFeatures[index] ? 'fa-chevron-up' : 'fa-chevron-down'} ml-2`}></i>
                    </h4>

                    {/* Render Table Header Only When Collapsed */}
                    {!tablesVisibility.keyFeatures[index] && (
                        <div className="table-header">
                            <table className="min-w-full table-auto border-collapse border border-gray-400 mt-1">
                                <thead className="bg-blue-100 rounded-xl">
                                    <tr className="text-center text-sm text-gray-600">
                                        {table.header.map((header, idx) => (
                                            <th
                                                key={idx}
                                                className="px-1 py-1 border-b border-gray-400 border-t border-l border-r"
                                            >
                                                {highlightText(header, searchTerms)}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                            </table>
                        </div>
                    )}

                    {/* Render Full Table with Data After Expansion */}
                    {tablesVisibility.keyFeatures[index] && (
                        <div className="table-data mt-4">
                            <table className="min-w-full table-auto border-collapse border border-gray-400 mt-1">
                                <thead className="bg-blue-100 rounded-xl">
                                    <tr className="text-center text-sm text-gray-600">
                                        {table.header.map((header, idx) => (
                                            <th
                                                key={idx}
                                                className="px-1 py-1 border-b border-gray-400 border-t border-l border-r"
                                            >
                                                {highlightText(header, searchTerms)}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {table.data.map((row, rowIndex) => (
                                        <tr key={rowIndex} className="hover:bg-gray-50 transition-colors">
                                            {row.map((cell, cellIndex) => (
                                                <td
                                                    key={cellIndex}
                                                    className="px-2 text-sm py-2 border-b border-gray-300 border-t border-l border-r"
                                                >
                                                    {highlightTableCell(cell, searchTerms)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {tablesVisibility.keyFeatures[index] && (
                        <div
                            className="arrow-toggle cursor-pointer mt-2 flex justify-center"
                            onClick={(e) => handleTableHeaderClick('keyFeatures', index, e)}
                        >
                            <i className="fas fa-chevron-up" />
                        </div>
                    )}

                </div>
            ))}

            {/* Search Strategies Tables */}
            {isOpen && hasTableContent(document.search_strategies) && document.search_strategies.length > 0 && document.search_strategies.map((table, index) => (
                <div key={`searchStrategy-${index}`} className="bg-white p-4 rounded-lg shadow-md mb-4">
                    <h4
                        className="text-lg font-semibold text-gray-700 mb-2 cursor-pointer"
                        onClick={(e) => handleTableHeaderClick('searchStrategies', index, e)} // Handle search strategies header click
                    >
                        Search Strategy {index + 1} <i className={`fas ${tablesVisibility.searchStrategies[index] ? 'fa-chevron-up' : 'fa-chevron-down'} ml-2`}></i>
                    </h4>

                    {/* Render Table Header Only When Collapsed */}
                    {!tablesVisibility.searchStrategies[index] && (
                        <div className="table-header">
                            <table className="min-w-full table-auto border-collapse border border-gray-400 mt-1">
                                <thead className="bg-blue-100 rounded-xl">
                                    <tr className="text-center text-sm text-gray-600">
                                        {table.header.map((header, idx) => (
                                            <th
                                                key={idx}
                                                className="px-1 py-1 border-b border-gray-400 border-t border-l border-r"
                                            >
                                                {highlightText(header, searchTerms)}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                            </table>
                        </div>
                    )}

                    {/* Render Full Table with Data After Expansion */}
                    {tablesVisibility.searchStrategies[index] && (
                        <div className="table-data mt-4">
                            <table className="min-w-full table-auto border-collapse border border-gray-400 mt-1">
                                <thead className="bg-blue-100 rounded-xl">
                                    <tr className="text-center text-sm text-gray-600">
                                        {table.header.map((header, idx) => (
                                            <th
                                                key={idx}
                                                className="px-1 py-1 border-b border-gray-400 border-t border-l border-r"
                                            >
                                                {highlightText(header, searchTerms)}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {table.data.map((row, rowIndex) => (
                                        <tr key={rowIndex} className="hover:bg-gray-50 transition-colors">
                                            {row.map((cell, cellIndex) => (
                                                <td
                                                    key={cellIndex}
                                                    className="px-2 text-sm py-2 border-b border-gray-300 border-t border-l border-r"
                                                >
                                                    {highlightTableCell(cell, searchTerms)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {tablesVisibility.searchStrategies[index] && (
                        <div
                            className="arrow-toggle cursor-pointer mt-2 flex justify-center"
                            onClick={(e) => handleTableHeaderClick('searchStrategies', index, e)}
                        >
                            <i className="fas fa-chevron-up" />
                        </div>
                    )}
                </div>
            ))}

            {/* Conditionally Render Bottom Arrow Icon */}
            {isOpen && (
                <div
                    className="arrow-toggle cursor-pointer mt-4 flex justify-center"
                    onClick={handleCardClick}
                >
                    <i className={`fas fa-chevron-up text-gray-400 text-xl`} />
                </div>
            )}
        </div>
    );
};

export default DocumentCard;